import { useAuth } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import {
  toast,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@ndd/ui";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { RegistrySignInAccount } from "@/lib/rd-registry-lanes";
import {
  ADD_RD_SUBTITLE,
  ADD_RD_TITLE,
  AddRdEmptyState,
  AddRdProviderStep,
  AddRdSupervisorStep,
  LinkedRdDialogBody,
  RegistryLanes,
  RegistryPageHead,
  RegistryRollupStrip,
  TombstoneRdDialogBody,
  TombstonedSection,
  type AddRdHeaderSlots,
  type EnrollableProviderView,
  type LinkedRemoval,
  type TombstoneRefusal,
  type TombstonedRdView,
} from "@/components/v2/rd-registry-parts";
import {
  RD_TOMBSTONED_TOAST_DETAIL,
  rdDeleteBody,
  rdDeleteTitle,
  rdHistoryKeptLine,
  rdTombstonedToast,
} from "../../../../shared/rd-registry-copy";
import type { RdRemoval } from "../../../../shared/rd-registry-removal";
import { AdminGuard, AdminNav } from "./shared";

const RD_NAME_CONFLICT_CODE = "rd_name_conflict";

interface RdDeleteBlockers {
  sessionCount: number;
  chatCount: number;
  observationCount: number;
  clinicalSessionCount: number;
  formProfileCount: number;
  formSnapshotCount: number;
  meetingNoteCount: number;
  meetingNoteShareCount: number;
}

interface TombstonedRdRow extends TombstonedRdView {
  blockers: RdDeleteBlockers;
}

/** The page reads only the counts, for "History kept"; never canDelete or reason. */
interface RdDeleteEligibility {
  blockers: RdDeleteBlockers;
}

interface AdminRdRow {
  id: string;
  slug: string;
  name: string;
  createdByUserId: string;
  createdByName: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  sessionCount: number;
  /** Read only for the "History kept" line; `removal` decides every dialog. */
  deleteEligibility: RdDeleteEligibility;
  removal: RdRemoval;
  createdAt: string;
  signInAccount: RegistrySignInAccount | null;
  healthieRecordUrl: string | null;
}

interface SupervisorOption {
  id: string;
  name: string;
}

const FIELD_BASE =
  "h-9 rounded-[10px] bg-background border-border text-foreground text-body px-3 placeholder:text-subtle shadow-none";

function apiErrorBody(error: unknown): { error?: string; code?: string } | null {
  if (
    error instanceof ApiRequestError &&
    error.body &&
    typeof error.body === "object"
  ) {
    return error.body as { error?: string; code?: string };
  }

  return null;
}

export function AdminRds(): ReactElement {
  return (
    <AdminGuard>
      <RdsClient />
    </AdminGuard>
  );
}

function EditRdFields({
  name,
  onNameChange,
  error,
  supervisorId,
  onSupervisorChange,
  supervisors,
}: {
  name: string;
  onNameChange: (value: string) => void;
  error: string | null;
  supervisorId: string;
  onSupervisorChange: (value: string) => void;
  supervisors: SupervisorOption[];
}): ReactElement {
  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor="edit-rd-name">Name</Label>
        <Input
          id="edit-rd-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className={FIELD_BASE}
          required
        />
      </div>
      {error && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-foreground">
          {error}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <Label htmlFor="edit-rd-supervisor">Supervisor</Label>
        <select
          id="edit-rd-supervisor"
          value={supervisorId}
          onChange={(e) => onSupervisorChange(e.target.value)}
          className={cn(FIELD_BASE, "w-full")}
        >
          <option value="">— None (unmapped) —</option>
          {supervisors.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <p className="text-micro text-muted-foreground mt-0.5">
          Unmapped RDs can still be seen by admins but block new VA uploads.
        </p>
      </div>
    </>
  );
}

function RdsClient(): ReactElement {
  const { getToken } = useAuth();
  const [editing, setEditing] = useState<AdminRdRow | null>(null);
  const [removing, setRemoving] = useState<AdminRdRow | null>(null);
  const [adding, setAdding] = useState(false);

  const rdsQuery = useQuery({
    queryKey: ["admin-rds"],
    queryFn: () => authenticatedApi<{ data: AdminRdRow[] }>(getToken, "/api/admin/rds"),
  });

  const supervisorsQuery = useQuery({
    queryKey: ["admin-supervisors"],
    queryFn: () =>
      authenticatedApi<{ data: { supervisors: SupervisorOption[] } }>(
        getToken,
        "/api/rds?include=supervisors"
      ),
  });
  const rds = rdsQuery.data?.data ?? [];
  const supervisors = supervisorsQuery.data?.data.supervisors ?? [];

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      <AdminNav />
      <RegistryPageHead
        action={
          <Button type="button" onClick={() => setAdding(true)}>
            + Add RD
          </Button>
        }
      />

      <RegistryRollupStrip rows={rds} />
      <RegistryLanes
        rows={rds}
        isLoading={rdsQuery.isLoading}
        onEdit={setEditing}
        onRemove={setRemoving}
      />
      <TombstonedRegistry />

      {editing && (
        <EditRdDialog
          rd={editing}
          supervisors={supervisors}
          onClose={() => setEditing(null)}
        />
      )}
      {removing && <RemoveRdDialog rd={removing} onClose={() => setRemoving(null)} />}
      {adding && (
        <AddRdDialog supervisors={supervisors} onClose={() => setAdding(false)} />
      )}
    </div>
  );
}

/** The collapsed Tombstoned section, with its own query (NO-695). */
function TombstonedRegistry(): ReactElement | null {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const tombstoned = useQuery({
    queryKey: ["admin-rds-tombstoned"],
    queryFn: () =>
      authenticatedApi<{ data: TombstonedRdRow[] }>(
        getToken,
        "/api/admin/rds/tombstoned"
      ),
  });

  return (
    <TombstonedSection
      rows={tombstoned.data?.data ?? []}
      open={open}
      onOpenChange={setOpen}
    />
  );
}

/**
 * The one Remove dialog (NO-695): the server's `removal` picks it, and the
 * clicked row keeps its kind while open. The switch is exhaustive, so a new
 * kind fails the type check.
 */
function RemoveRdDialog({ rd, onClose }: { rd: AdminRdRow; onClose: () => void }) {
  const removal = rd.removal;

  switch (removal.kind) {
    case "delete":
      return <DeleteRdDialog rd={rd} onClose={onClose} />;
    case "tombstone":
      return <TombstoneRdDialog rd={rd} onClose={onClose} />;
    case "linked":
      return <LinkedRdDialog rd={rd} removal={removal} onClose={onClose} />;
    default:
      return assertNever(removal);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unknown RD removal: ${JSON.stringify(value)}`);
}

/** Focuses the element with `id` when a dialog opens, instead of the first in DOM order. */
function focusOnOpen(id: string) {
  return (event: Event) => {
    event.preventDefault();
    document.getElementById(id)?.focus();
  };
}

const DELETE_INVALIDATES = [["admin-rds"], ["rds"], ["rd-picker"]];

function useRdDelete(
  rd: AdminRdRow,
  onClose: () => void,
  setError: (message: string) => void
) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      authenticatedApi(getToken, `/api/admin/rds/${rd.id}`, { method: "DELETE" }),
    onSuccess: () => {
      for (const queryKey of DELETE_INVALIDATES)
        void queryClient.invalidateQueries({ queryKey });

      toast.success(`Deleted ${rd.name}`);
      onClose();
    },
    onError: (err) => {
      // A race: the RD gained history after the list loaded. Refetch so the
      // row's removal catches up; this dialog keeps its kind.
      void queryClient.invalidateQueries({ queryKey: ["admin-rds"] });
      setError(apiErrorBody(err)?.error ?? "Failed to delete RD.");
    },
  });
}

function DeleteRdDialog({ rd, onClose }: { rd: AdminRdRow; onClose: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const remove = useRdDelete(rd, onClose, setError);
  const isPending = remove.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    remove.mutate();
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent onOpenAutoFocus={focusOnOpen("delete-rd-cancel")}>
        <DialogHeader>
          <DialogTitle>{rdDeleteTitle(rd.name)}</DialogTitle>
          <DialogDescription>{rdDeleteBody(rd.name)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          {error && (
            <div className="rounded-lg border border-error/30 bg-danger-soft px-3 py-2 text-sm text-foreground">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button
              id="delete-rd-cancel"
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={isPending}>
              {isPending ? "Deleting..." : "Delete RD"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LinkedRdDialog({
  rd,
  removal,
  onClose,
}: {
  rd: AdminRdRow;
  removal: LinkedRemoval;
  onClose: () => void;
}) {
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent onOpenAutoFocus={focusOnOpen("linked-rd-close")}>
        <LinkedRdDialogBody
          rdName={rd.name}
          removal={removal}
          signInAccount={rd.signInAccount}
          healthieRecordUrl={rd.healthieRecordUrl}
          onClose={onClose}
          headerSlots={REMOVE_HEADER_SLOTS}
        />
      </DialogContent>
    </Dialog>
  );
}

const TOMBSTONE_INVALIDATES = [
  ["admin-rds"],
  ["admin-rds-tombstoned"],
  ["rds"],
  ["rd-picker"],
  ["rd-options"],
  ["migration-rds"],
];

function useRdTombstone(
  rd: AdminRdRow,
  onClose: () => void,
  setRefusal: (refusal: TombstoneRefusal) => void
) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { name: string; reason: string }) =>
      authenticatedApi(getToken, `/api/admin/rds/${rd.id}/tombstone`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      for (const queryKey of TOMBSTONE_INVALIDATES)
        void queryClient.invalidateQueries({ queryKey });

      toast.success(rdTombstonedToast(rd.name), {
        description: RD_TOMBSTONED_TOAST_DETAIL,
      });
      onClose();
    },
    onError: (err) => {
      const body = apiErrorBody(err);

      if (body?.error) {
        // A race (a link or a tombstone landed after the list loaded):
        // refetch so the row's removal catches up.
        void queryClient.invalidateQueries({ queryKey: ["admin-rds"] });
        setRefusal({ code: body.code, error: body.error });

        return;
      }

      toast.error("Failed to tombstone RD");
    },
  });
}

const REMOVE_HEADER_SLOTS: AddRdHeaderSlots = {
  Title: DialogTitle,
  Description: DialogDescription,
};

function TombstoneRdDialog({ rd, onClose }: { rd: AdminRdRow; onClose: () => void }) {
  const [typedName, setTypedName] = useState("");
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<TombstoneRefusal | null>(null);
  const tombstone = useRdTombstone(rd, onClose, setRefusal);
  const isPending = tombstone.isPending;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent onOpenAutoFocus={focusOnOpen("tombstone-rd-name")}>
        <TombstoneRdDialogBody
          rdName={rd.name}
          historyLine={rdHistoryKeptLine(rd.deleteEligibility.blockers)}
          typedName={typedName}
          onTypedNameChange={(value) => {
            setTypedName(value);
            setRefusal(null);
          }}
          reason={reason}
          onReasonChange={(value) => {
            setReason(value);
            setRefusal(null);
          }}
          refusal={refusal}
          isPending={isPending}
          onCancel={onClose}
          onConfirm={() => tombstone.mutate({ name: typedName, reason })}
          headerSlots={REMOVE_HEADER_SLOTS}
        />
      </DialogContent>
    </Dialog>
  );
}

function useRdUpdate({
  rd,
  name,
  supervisorId,
  onClose,
  setError,
}: {
  rd: AdminRdRow;
  name: string;
  supervisorId: string;
  onClose: () => void;
  setError: (message: string) => void;
}) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      authenticatedApi(getToken, `/api/admin/rds/${rd.id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, supervisedByUserId: supervisorId || null }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-rds"] });
      toast.success(`Updated ${name}`);
      onClose();
    },
    onError: (err) => {
      const body = apiErrorBody(err);

      if (body?.code === RD_NAME_CONFLICT_CODE) {
        setError(body.error ?? "An RD with this name already exists.");

        return;
      }

      toast.error(body?.error ?? "Failed to save");
    },
  });
}

function EditRdDialog({
  rd,
  supervisors,
  onClose,
}: {
  rd: AdminRdRow;
  supervisors: SupervisorOption[];
  onClose: () => void;
}) {
  const [name, setName] = useState(rd.name);
  const [supervisorId, setSupervisorId] = useState<string>(rd.supervisorId ?? "");
  const [error, setError] = useState<string | null>(null);

  const update = useRdUpdate({ rd, name, supervisorId, onClose, setError });
  const isPending = update.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    update.mutate();
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit RD</DialogTitle>
          <DialogDescription>
            Rename the RD or reassign to a different supervisor. Session history is
            preserved.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <EditRdFields
            name={name}
            onNameChange={(value) => {
              setName(value);
              setError(null);
            }}
            error={error}
            supervisorId={supervisorId}
            onSupervisorChange={setSupervisorId}
            supervisors={supervisors}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// NO-651: the Add RD dialog enrolls an existing Healthie provider. Step bodies
// live in rd-registry-parts.tsx; this container owns state, queries and the
// mutation.

const DIALOG_HEADER_SLOTS: AddRdHeaderSlots = {
  Title: DialogTitle,
  Description: DialogDescription,
};

const PROVIDER_NOT_ENROLLABLE_CODE = "provider_not_enrollable";

function matchesProvider(provider: EnrollableProviderView, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [provider.name, provider.email ?? ""].some((field) =>
    field.toLowerCase().includes(needle)
  );
}

function useEnrollableProviders() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["admin-enrollable-providers"],
    queryFn: () =>
      authenticatedApi<{ data: EnrollableProviderView[] }>(
        getToken,
        "/api/admin/rds/enrollable"
      ),
  });
}

function useRdEnroll({
  onClose,
  setError,
  refetchEnrollable,
}: {
  onClose: () => void;
  setError: (message: string) => void;
  refetchEnrollable: () => void;
}) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { providerId: string; supervisedByUserId: string }) =>
      authenticatedApi<{ data: { id: string; name: string } }>(
        getToken,
        "/api/admin/rds",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: (result) => {
      for (const queryKey of [
        ["admin-rds"],
        ["admin-enrollable-providers"],
        ["rds"],
        ["rd-picker"],
        ["migration-rds"],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }

      toast.success(`Added ${result.data.name}`);
      onClose();
    },
    onError: (err) => {
      const body = apiErrorBody(err);
      if (body?.code === PROVIDER_NOT_ENROLLABLE_CODE) refetchEnrollable();

      if (
        body?.code === RD_NAME_CONFLICT_CODE ||
        body?.code === PROVIDER_NOT_ENROLLABLE_CODE
      ) {
        setError(body.error ?? "Failed to add RD");

        return;
      }

      toast.error("Failed to add RD");
    },
  });
}

function AddRdDialog({
  supervisors,
  onClose,
}: {
  supervisors: SupervisorOption[];
  onClose: () => void;
}): ReactElement {
  const [query, setQuery] = useState("");
  const [providerId, setProviderId] = useState<string | null>(null);
  // Step 2 holds the provider chosen on Next, so a refetch after a stale pick
  // cannot pull the step (and its error notice) out from under the admin.
  const [chosen, setChosen] = useState<EnrollableProviderView | null>(null);
  const [supervisorId, setSupervisorId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const enrollable = useEnrollableProviders();
  const enroll = useRdEnroll({
    onClose,
    setError,
    refetchEnrollable: () => void enrollable.refetch(),
  });
  const providers = enrollable.data?.data ?? [];
  const isPending = enroll.isPending;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !isPending && onClose()}>
      <DialogContent>
        {chosen ? (
          <AddRdSupervisorStep
            provider={chosen}
            supervisors={supervisors}
            supervisorId={supervisorId}
            onSupervisorChange={(id) => {
              setSupervisorId(id);
              setError(null);
            }}
            error={error}
            isPending={isPending}
            onBack={() => {
              setChosen(null);
              setError(null);
            }}
            onSubmit={() =>
              supervisorId &&
              enroll.mutate({ providerId: chosen.id, supervisedByUserId: supervisorId })
            }
            headerSlots={DIALOG_HEADER_SLOTS}
          />
        ) : (
          <AddRdProviderChoice
            providers={providers}
            isLoaded={enrollable.isSuccess}
            query={query}
            onQueryChange={setQuery}
            providerId={providerId}
            onSelect={setProviderId}
            onClose={onClose}
            onNext={setChosen}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddRdProviderChoice({
  providers,
  isLoaded,
  query,
  onQueryChange,
  providerId,
  onSelect,
  onClose,
  onNext,
}: {
  providers: EnrollableProviderView[];
  isLoaded: boolean;
  query: string;
  onQueryChange: (value: string) => void;
  providerId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
  onNext: (provider: EnrollableProviderView) => void;
}): ReactElement {
  if (!isLoaded) {
    return (
      <DialogHeader>
        <DialogTitle>{ADD_RD_TITLE}</DialogTitle>
        <DialogDescription>{ADD_RD_SUBTITLE}</DialogDescription>
      </DialogHeader>
    );
  }

  if (providers.length === 0) {
    return <AddRdEmptyState onClose={onClose} headerSlots={DIALOG_HEADER_SLOTS} />;
  }

  const selected = providers.find((candidate) => candidate.id === providerId);

  return (
    <AddRdProviderStep
      providers={providers.filter((candidate) => matchesProvider(candidate, query))}
      query={query}
      onQueryChange={onQueryChange}
      selectedId={selected ? selected.id : null}
      onSelect={onSelect}
      onCancel={onClose}
      onNext={() => selected && onNext(selected)}
      headerSlots={DIALOG_HEADER_SLOTS}
    />
  );
}
